import type { IApiError, IApiMeta } from '@/shared/types/api'

/**
 * Lab studies shown on the donor card — the read-only view of every lab order
 * raised for a donor (`GET /v1/donor-card/{donor_id}/lab-studies`). Rows carry
 * only a result summary per analysis; the full parameter table lives behind the
 * lab order detail endpoint (`/v1/lab/orders/{id}`).
 */

export const DONOR_LAB_STUDY_SCOPES = ['all', 'current', 'archive'] as const

export type TDonorLabStudyScope = (typeof DONOR_LAB_STUDY_SCOPES)[number]

/**
 * `filter` is typed as a plain string server-side, so the scope values the tab
 * sends are kept here as the single place to adjust if the backend settles on a
 * different vocabulary. `all` sends no filter at all.
 */
export const DONOR_LAB_STUDY_SCOPE_FILTER: Record<
  TDonorLabStudyScope,
  string | undefined
> = {
  all: undefined,
  current: 'current',
  archive: 'archive',
}

export interface IDonorLabStudiesParams {
  donor_id: string
  filter?: string
  page: number
  size: number
}

export interface IDonorLabStudyAnalysis {
  name: string
  loinc_code: string
  status: string
  status_label: string
  result: string | null
  result_date: string | null
  executor: string | null
}

export interface IDonorLabStudyItem {
  id: string
  order_number: string
  visit_id: string
  /** False for orders raised on earlier visits — rendered dimmed and read-only. */
  is_current_visit: boolean
  date: string
  laboratory: string
  // Mirrors the lab orders list, where `analyses` is missing on some responses.
  analyses?: IDonorLabStudyAnalysis[]
  lab_technician: string | null
  admission_status: string
  admission_status_label: string
}

/** Admission verdict for the current visit, taken off the lab results. */
export interface IDonorLabStudiesSummary {
  visit_id: string
  donor_category: string | null
  can_set_admission: boolean
  all_analyses_confirmed: boolean
  admission_decision: string | null
  admission_decision_label: string | null
  admission_decided_by: string | null
  admission_decided_at: string | null
  admission_reason: string | null
}

export interface IDonorLabStudiesResponse {
  success: boolean
  data: IDonorLabStudyItem[]
  errors: IApiError[]
  meta: IApiMeta
  page: number
  hasNext: boolean
  totalSize: number
  totalPages: number
  summary: IDonorLabStudiesSummary | null
}

// ── Admission decision ───────────────────────────────────────────────────────

export const EDonorLabAdmissionDecision = {
  TO_DONATION: 'TO_DONATION',
  MEDICAL_DEFERRAL: 'MEDICAL_DEFERRAL',
} as const

export type TDonorLabAdmissionDecision =
  (typeof EDonorLabAdmissionDecision)[keyof typeof EDonorLabAdmissionDecision]

/** `reason` / `deferral_end_date` only carry meaning for a medical deferral. */
export interface IDonorLabAdmissionDecisionPayload {
  decision: TDonorLabAdmissionDecision
  reason?: string
  deferral_end_date?: string
  organization_id?: string
}

export interface IDonorLabAdmissionDecisionResult {
  decision: string
  visit_id: string
  decision_label: string
  decided_by: string
  decided_at: string
}

export interface IDonorLabAdmissionDecisionResponse {
  success: boolean
  data: IDonorLabAdmissionDecisionResult
  errors: IApiError[]
  meta: IApiMeta
}
